import React, { useEffect } from 'react'
import qs from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useLazyQuery } from '@apollo/react-hooks'

import {
  SIGN_UP_SCREENS,
  VOUCHERS
} from '../../../../constants'
import { STORAGE_KEY_VOUCHER_APPLICABLE_PLANS } from '../../../../storage-keys'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { handleGATrack } from '../../utils/handle-ga-track'
import { signUp } from '../../../../lib/analytics/events'
import VOUCHER_WITHOUT_SKU_QUERY from '../../../../../graphql/queries/voucher-without-sku.gql'
import Input from '../../../../components/form-fields/input'
import Button from '../../../../components/button'
import Step from '../step'
import CheckListItem from './check-list-item'
import styles from '../sign-up.css'

/**
 * Plan intro screen with benefits list and voucher form
 */
const PlanIntro = () => {
  const currentScreen = SIGN_UP_SCREENS.PLAN_INTRO
  const history = useHistory()
  const location = useLocation()

  const [
    voucherWithoutSkuQuery,
    { data: voucherData, loading: voucherLoading, error: voucherError }
  ] = useLazyQuery(VOUCHER_WITHOUT_SKU_QUERY, {
    fetchPolicy: 'network-only'
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    setError
  } = useForm({
    defaultValues: {
      voucherCode: ''
    }
  })

  // Track signup flow step view (sequence & label)
  useEffect(() => {
    signUp.stepViewed(currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel)
  }, [])

  // Handle voucher validation response
  useEffect(() => {
    if (voucherError) {
      const errorMessage = voucherError.graphQLErrors
        ? getGQLErrorMsg(voucherError)
        : voucherError.message

      setError('voucherCode', {
        message: errorMessage
      })

      // Track voucher validation error
      handleGATrack(
        { name: 'voucherCode', value: '' },
        { message: errorMessage },
        currentScreen.datalayerStepSequence,
        currentScreen.datalayerStepLabel,
        true
      )
    }

    if (voucherData && !voucherLoading) {
      const { voucherWithoutSku } = voucherData

      // Store applicable plans in session storage
      if (voucherWithoutSku?.applicablePlans) {
        sessionStorage.setItem(
          STORAGE_KEY_VOUCHER_APPLICABLE_PLANS,
          voucherWithoutSku.applicablePlans.toString()
        )
      }

      // Navigate to plan selection page with voucher in query string
      history.push({
        pathname: SIGN_UP_SCREENS.PLAN_SELECT.path,
        search: qs.stringify({
          ...qs.parse(location.search),
          voucher: voucherWithoutSku?.code
        })
      })
    }
  }, [voucherData, voucherError, voucherLoading, setError, history, location])

  const onSubmit = async (formData) => {
    const { voucherCode } = formData

    // Clear the voucher info in session storage
    sessionStorage.removeItem(STORAGE_KEY_VOUCHER_APPLICABLE_PLANS)

    if (voucherCode && voucherCode.trim() !== '') {
      // validate voucher then save voucher for plan selection
      voucherWithoutSkuQuery({
        variables: {
          voucherCode: voucherCode.trim()
        }
      })
    } else {
      history.push(SIGN_UP_SCREENS.PLAN_SELECT.path)
    }
  }

  return (
    <>
      <div className={styles.topContainer}>
        <Step
          current={currentScreen.step}
          total={currentScreen.totalSteps}
        />
        <div className={styles.header}>Choose a plan that suits how you watch</div>
      </div>
      <div className={styles.container}>
        <div className={styles.content}>
          <CheckListItem>
            Unlimited access to TV series and movies
          </CheckListItem>
          <CheckListItem>
            Flexible cancelling at any time, effective at the end of your billing period*
          </CheckListItem>
          <CheckListItem>
            Watch on all your devices
          </CheckListItem>
        </div>
      </div>
      <div className={styles.container}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.content}>
          <div className={styles.subHeader}>
            Do you have a voucher code? (optional)
          </div>

          <>
            <Controller
              name="voucherCode"
              control={control}
              render={({ field }) => (
                <Input
                  label="Enter your voucher code"
                  {...field}
                  error={errors[field.name]?.message}
                  onBlur={() => {
                    field.onBlur()
                    handleGATrack(field, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
                  }}
                  className={styles.inputNoMarginBottom}
                  disabled={voucherLoading}
                  data-e2e="voucher-signup-input"
                  autoComplete="off"
                />
              )}
            />
            <div className={styles.link}>
              <a
                href={VOUCHERS.HELP_ARTICLE}
                target="_blank"
                rel="noopener noreferrer"
              >
                What&apos;s this?
              </a>
            </div>
          </>

          <Button
            className={styles.submit}
            text="Choose a plan"
            type="submit"
            disabled={voucherLoading}
            e2eIdentifier="choose-plan-button"
          />

          {errors.root?.formError && (
            <p className={styles.errorMessage}>
              {errors.root.formError.message}
            </p>
          )}
        </form>
      </div>
    </>
  )
}

export default PlanIntro
